// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { GetInvoiceDetailsChargesQuery } from '../queries/getInvoiceDetailsCharges.query.generated';

import {
  amountDueArgroups,
  buildAmountObjectFromAggregates,
  getProductCharges,
  groupChargesByMove,
} from '@features/invoices/services/calcServiceCharges';
import { useInvoiceAmountDue } from '@features/invoices/hooks/useInvoiceAmountDue';
import useCsvGeneration from '@features/invoices/hooks/useCsvGeneration';
import usePdfGeneration from '@features/invoices/hooks/usePdfGeneration';

import { PROGRAM_INVOICE_TYPE, condensePerUseCharges } from '@hopdrive/sdk-ar';

import { css } from '@emotion/css';
import { Grid, Paper, Theme, Typography, useTheme } from '@mui/material';
import { BiDollarCircle, BiDownload, BiFile } from 'react-icons/bi';

import Spacer from '@components/Utils/Spacer';
import Toolbar from '@components/Utils/Toolbar';

import InvoiceDetailsCalculations from '@features/invoices/components/InvoiceDetailsCalculations';
import InvoiceProgramBreakdown from '@features/invoices/components/InvoiceProgramBreakdown';
import InvoiceDetailsInfoCharges from '@features/invoices/components/InvoiceDetailsInfoCharges';
import InvoiceDetailsPayments from '@features/invoices/components/InvoiceDetailsPayments';
import InvoiceDetailsProductsCharges from '@features/invoices/components/InvoiceDetailsProductsCharges';
import InvoiceDetailsTableCharges from '@features/invoices/components/InvoiceDetailsTableCharges';
import InvoiceRooftopSection from '@features/invoices/components/InvoiceRooftopSection';

import DisputeModal from '@features/invoices/components/DisputeModal';
import PayNowModal from '@features/invoices/components/PayNowModal';

// TYPES ---------------------------------------------------------------- //

type InvoiceDetailsContentChargesProps = {
  invoice?: GetInvoiceDetailsChargesQuery['arinvoices_by_pk'];
  refetch?: () => Promise<void>;
};

// The modals own the shape of their input; this component only hands it through.
type ModalState = { open?: boolean; input?: unknown };

// COMPONENT ---------------------------------------------------------------- //

const InvoiceDetailsContentCharges = ({
  invoice,
  refetch,
}: InvoiceDetailsContentChargesProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { generateCSV, generateEnhancedCSV } = useCsvGeneration();
  const { generatePDF } = usePdfGeneration();

  // Dispute Modal
  const [disputeModal, setDisputeModal] = React.useState<ModalState>({
    open: false,
    input: null,
  });
  const handleDisputeModalOpen = (input: unknown = null) => {
    setDisputeModal({ ...disputeModal, open: true, input: input });
  };
  const handleDisputeModalClose = () => {
    setDisputeModal({ ...disputeModal, open: false, input: null });
  };

  // Pay-Now Modal
  const [payNowModal, setPayNowModal] = React.useState<ModalState>({
    open: false,
    input: null,
  });
  const handlePayNowModalOpen = (input: unknown = null) => {
    setPayNowModal({ ...payNowModal, open: true, input: input });
  };
  const handlePayNowModalClose = () => {
    setPayNowModal({ ...payNowModal, open: false, input: null });
  };

  const argroups = invoice?.argroups || [];

  // The Amount Due is sdk-ar's answer over every charge the totals count. Memoized on the query
  // result so a refetch asks again and a re-render does not.
  const dueArgroups = React.useMemo(() => (invoice ? amountDueArgroups(invoice) : null), [invoice]);
  const amountDue = useInvoiceAmountDue(dueArgroups);

  // The totals from the aggregates, with sdk-ar's Amount Due (consistent with the sidebar)
  const amount = buildAmountObjectFromAggregates(invoice, amountDue);

  // Move rows, products and the condensed per-use line, from sdk-ar's shared grouping
  const moveGroups = groupChargesByMove(argroups);
  const products = getProductCharges(argroups);
  const perUseLine = condensePerUseCharges(argroups);

  const isRideshareInvoice = invoice?.invoice_type?.includes('rideshare');
  // A program invoice bills its payer for many rooftops, so it reads by rooftop rather than move by move.
  const isProgramInvoice = invoice?.invoice_type === PROGRAM_INVOICE_TYPE;

  // Extract all archarge IDs from the invoice argroups
  const archargeIds = argroups
    .filter(group => group.status !== 'historical')
    .flatMap(group => group.archarges || [])
    .filter(charge => charge.active)
    .map(charge => charge.id);

  const actions = [
    {
      label: `Pay Now`,
      icon: <BiDollarCircle className={cls.menuIcon} />,
      color: theme?.palette?.primary?.main,
      handler: () => handlePayNowModalOpen({ invoice, ...amount, archargeIds }),
      disabled: !amount?.payable,
    },
    {},
    {
      label: `Generate CSV`,
      icon: <BiFile className={cls.menuIcon} />,
      handler: () => generateCSV(invoice),
    },
    ...(!isRideshareInvoice
      ? [
          {
            label: `Generate Enhanced CSV`,
            icon: <BiFile className={cls.menuIcon} />,
            handler: () => generateEnhancedCSV(invoice),
          },
        ]
      : []),
    {
      label: `Generate PDF`,
      icon: <BiDownload className={cls.menuIcon} />,
      handler: () => generatePDF(invoice),
    },
  ];

  return (
    <>
      <DisputeModal
        input={disputeModal.input}
        open={disputeModal.open}
        onClose={handleDisputeModalClose}
        refetch={refetch}
      />

      <PayNowModal
        input={payNowModal?.input}
        open={payNowModal?.open}
        onClose={handlePayNowModalClose}
        refetch={refetch}
      />

      <div className={cls.content}>
        <Toolbar title='Invoice Details' refetch={refetch} actions={invoice ? actions : []} />

        <Spacer />

        {!invoice ? (
          <Paper variant='custom' className={cls.notFound}>
            <Typography style={{ fontSize: `20px`, fontWeight: 500, color: theme?.palette?.text?.disabled }}>
              No invoice found/selected
            </Typography>
          </Paper>
        ) : (
          <>
            <InvoiceDetailsInfoCharges invoice={invoice} />

            <Spacer />

            {isProgramInvoice ? (
              <>
                <InvoiceRooftopSection
                  invoice={invoice}
                  amount={amount}
                  archargeIds={archargeIds}
                  handlePayNowModalOpen={handlePayNowModalOpen}
                  handleDisputeModalOpen={handleDisputeModalOpen}
                />

                <Spacer />
              </>
            ) : (
              <>
                {(products && products?.length) || perUseLine ? (
                  <>
                    <InvoiceDetailsProductsCharges products={products} perUseLine={perUseLine} />

                    <Spacer />
                  </>
                ) : null}

                {moveGroups && moveGroups?.length ? (
                  <>
                    <InvoiceDetailsTableCharges
                      invoice={invoice}
                      amount={amount}
                      archargeIds={archargeIds}
                      handlePayNowModalOpen={handlePayNowModalOpen}
                      handleDisputeModalOpen={handleDisputeModalOpen}
                    />

                    <Spacer />
                  </>
                ) : null}
              </>
            )}

            <Grid container spacing={2}>
              <Grid size={{ md: 8, sm: 6, xs: 12 }}>
                <InvoiceDetailsPayments invoice={invoice} />
              </Grid>
              <Grid size={{ md: 4, sm: 6, xs: 12 }}>
                <InvoiceDetailsCalculations amount={amount} />
                <InvoiceProgramBreakdown
                  effectiveConfig={invoice.customer?.effective_config}
                  scenarioIds={invoice.argroups.map(argroup => argroup.scenario_id)}
                />
              </Grid>
            </Grid>
            <div style={{ marginTop: '16px' }}>
              <Typography className={cls.lateFeeDisclaimer}>
                Invoice Payment should be remitted by the due date. Payments received after the due date may be subject
                to late fees.
              </Typography>
            </div>
          </>
        )}
      </div>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (_theme?: Theme) => ({
  content: css`
    position: relative;
  `,

  notFound: css`
    padding: 24px;
    text-align: center;
  `,

  menuIcon: css`
    display: block;
    margin-top: -1px;
    margin-right: 8px;
  `,
  lateFeeDisclaimer: css`
    text-align: center;
    font-size: 12px !important;
    font-weight: 500 !important;
    margin-top: 16px;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceDetailsContentCharges;
